/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import {
    TRACKING_ERROR_TYPE_TECHNICAL,
    TRACKING_ERROR_TYPE_VALIDATION,
    TRACKING_TARGET_AUDIENCE_CONSUMER,
} from '@vfde-sails/tracking';
import {
    BTX_TYPE_NEW_CONTRACT,
    CustomerType,
} from '@vfde-sails/constants';
import {
    JarvisAvailability,
    JarvisOption,
    JarvisPriceMap,
    JarvisProduct,
} from '../interface';
import { JarvisPriceType } from '../enum';
import {
    TrackingBaseItem,
    TrackingForm,
    TrackingOrder,
    TrackingOrderProduct,
} from './interface';
import { convertToTrackingPrice } from '../util/formatter';
import {
    getSelectedTariff,
    isSelectedAvailable,
} from '../container/App/selector';
import { AppState } from '../container/App/interface';
import {
    TrackingFootprint,
    TrackingFormStatus,
    TrackingFormStepName,
    TrackingProductType,
} from './enum';
import { CableProvider } from '@vfde-sails/avc-core';
import { ErrorType } from '../container/App/enum';
import { isYoungPromoActive } from '../util/cms';

/**
 * Get the corresponding key for the price of a tariff/fee.
 *
 * @param type - Price type of the tariff/fee.
 * @returns Key for the tracking payload ("price_once" or "price_monthly")
 */
export const getTrackingPriceKey = (type: JarvisPriceType) => type === JarvisPriceType.ONCE
    ? 'price_once'
    : 'price_monthly';

/**
 * Get the corresponding key for the option picker tracking.
 *
 * @param isSwitcherOn - Status of the switcher.
 * @returns Key for the tracking payload ("selectedOption" or "removedOption")
 */
export const getSwitcherActionKey = (isSwitcherOn: boolean) => isSwitcherOn ? 'selectedOption' : 'removedOption';

/**
 * Converts a Jarvis product into a tracking item.
 *
 * @param tariff - Jarvis product
 * @param tariff.id - Metacode of the product
 * @param tariff.productType - Type of the product
 * @param tariff.price - Price object of the product
 * @param tariff.duration - Duration of the product
 * @returns Tracking item
 */
export const toTrackingItem = ({ id, productType, price, duration }: JarvisProduct): TrackingBaseItem => ({
    sku: id,
    name: id,
    type: productType,
    duration,
    [getTrackingPriceKey(price.type)]: convertToTrackingPrice(price.value, productType),
});

const toTrackingOption = ({ id, trackingType, price }: JarvisOption): TrackingOrderProduct => ({
    sku: id,
    name: id,
    type: trackingType,
    proposition: 1,
    units: 1,
    ...price && {
        [getTrackingPriceKey(price.type)]: convertToTrackingPrice(price.value, trackingType),
    },
});

/**
 * Gets and converts the options to tracking data in a product by the given Jarvis Price Map.
 *
 * @param options - Options of the main product.
 * @param priceMap - The Jarvis Price Map for the tracked option.
 * @returns Tracking option array
 */
export const getTrackingOptions = (options: JarvisOption[], priceMap?: JarvisPriceMap) => {
    const optionArray: TrackingOrderProduct[] = [];

    if (!priceMap || !options.length) {
        return optionArray;
    }

    for (const feeId of priceMap.ids) {
        const option = options.find(item => item.id === feeId);
        option && optionArray.push(toTrackingOption(option));
    }

    return optionArray;
};

/**
 * Converts a Jarvis product into an aProduct for tracking.
 *
 * @param tariff - Jarvis product.
 * @returns aProduct tracking object.
 */
export const getTrackingProduct = (tariff: JarvisProduct): TrackingOrderProduct => ({
    ...toTrackingItem(tariff),
    proposition: 1,
    units: 1,
});

const getStairwayTrackingItem = ({ id, price }: JarvisProduct): TrackingOrderProduct | null => price.stairway ? {
    sku: `${id}-discount`,
    name: `business action: stairway price of ${id}`,
    type: 'discount',
    price_monthly: convertToTrackingPrice(price.value - price.stairway.value, 'discount'),
    duration: price.stairway.period + 1,
    units: 1,
    proposition: 1,
} : null;

/**
 * Returns a copy of the page levels from the window object.
 */
export const pageLevels = window.VF?.siteStructure
    ? [...window.VF.siteStructure]
    : [];

/**
 * Returns the hotline numbers from the window object.
 */
export const getHotlineNumbers = () => window.VF?.hotlineNumbers || [];

const getTrackingItemsForProduct = (
    tariff: JarvisProduct | undefined,
    isGigakombi?: boolean,
    isFriendsAndFamily?: boolean,
    isYoung?: boolean,
): TrackingOrderProduct[] => {
    if (!tariff) {
        return [];
    }

    const stairwayDiscount = getStairwayTrackingItem(tariff);
    const items = [
        getTrackingProduct(tariff),
        ...getTrackingOptions(tariff.options, tariff.provisionFee),
        ...getTrackingOptions(tariff.options, tariff.routerDeliveryFee),
        ...getTrackingOptions(tariff.options, tariff.onlineBonus),
        ...getTrackingOptions(tariff.options, tariff.residentBonus),
    ];

    stairwayDiscount && items.push(stairwayDiscount);
    isGigakombi && items.push(...getTrackingOptions(tariff.options, tariff.gigakombiDiscount));
    isFriendsAndFamily && items.push(...getTrackingOptions(tariff.options, tariff.friendsAndFamilyDiscount));
    isYoung && !isYoungPromoActive() && items.push(...getTrackingOptions(tariff.options, tariff.youngDiscount));
    isYoung && isYoungPromoActive() && items.push(...getTrackingOptions(tariff.options, tariff.youngPromoDiscount));

    return items;
};

/**
 * Returns the oOrder tracking property based on the current state.
 */
export const getTrackingOrderObject = (state: AppState): TrackingOrder => {
    const { selectedTVProduct, address, isGigakombi, isFriendsAndFamily, isYoung } = state;
    const selectedTariff = getSelectedTariff(state);

    if (!selectedTariff) {
        return { aProducts: [], aPropositions: [] };
    }

    const oOrder: TrackingOrder = {
        aPropositions: [{
            id: 1,
            name: selectedTariff.id,
            target_audience: TRACKING_TARGET_AUDIENCE_CONSUMER,
            retention: BTX_TYPE_NEW_CONTRACT,
            product_type: selectedTariff.productType,
            product_line: '2p - internet & phone',
            product_category: 'bundle',
            units: 1,
        }],
        aProducts: [
            ...getTrackingItemsForProduct(selectedTariff, isGigakombi, isFriendsAndFamily, isYoung),
            ...getTrackingItemsForProduct(selectedTVProduct),
        ],
    };

    if (address?.zipcode) {
        oOrder.zipcode = address.zipcode;
    }

    return oOrder;
};

/**
 * Returns the form step for tracking by the name of the form step.
 *
 * @param stepName - Name of the form step.
 * @returns Form step number.
 */
export const getFormStepByName = (stepName: TrackingFormStepName) => {
    switch (stepName) {
        case TrackingFormStepName.SHOW:
            return 1;
        case TrackingFormStepName.START:
            return 2;
        case TrackingFormStepName.COMPLETE:
            return 3;
    }
};

/**
 * Returns the form status(es) for tracking by the name of the form step.
 *
 * @param stepName - Name of the form step.
 * @returns Form status array.
 */
export const getFormStatusByName = (stepName: TrackingFormStepName) => {
    switch (stepName) {
        case TrackingFormStepName.SHOW:
            return [TrackingFormStatus.START];
        case TrackingFormStepName.START:
            return [];
        case TrackingFormStepName.COMPLETE:
            return [
                TrackingFormStatus.END,
                TrackingFormStatus.SUCCESS,
            ];
    }
};

/**
 * Returns the formatted tracking footprint
 */
export const getTrackingFootprint = (value: CableProvider | CustomerType) => {
    switch (value) {
        case 'KDG':
        case 'VKD':
            return TrackingFootprint.KDG;
        case 'UM':
        case 'UME':
            return TrackingFootprint.UME;
        case 'DSL':
            return TrackingFootprint.DSL;
        default:
            return undefined;
    }
};

/**
 * Get available technologies
 */
export const getAvailableProductTypes = (availability: JarvisAvailability): TrackingProductType[] => {
    const result: TrackingProductType[] = [];

    if (availability.cable) {
        result.push(TrackingProductType.KDG);
    }

    if (availability.cableUnity) {
        result.push(TrackingProductType.UME);
    }

    if (availability.dsl) {
        result.push(TrackingProductType.DSL);
    }

    if (availability.fiber) {
        result.push(TrackingProductType.FIBER);
    }

    return result;
};

/**
 * Returns the AVC result specific props (step 3)
 */
export const getFormFieldsByAvailability = (state: AppState, step: TrackingFormStepName): Partial<TrackingForm> => {
    if (step !== TrackingFormStepName.COMPLETE) {
        return {};
    }

    const { alternativeTariff, selectedTariffId, availableTariffs, marketability, availability } = state;
    const fastestTariff = alternativeTariff || availableTariffs.find(tariff => tariff.id === selectedTariffId);

    const payload: Partial<TrackingForm> = {
        transfer_point_available: false,
        product_footprint: marketability && getTrackingFootprint(marketability.providerType),
        self_installation_available: Boolean(marketability?.isSelfInstall),
        tv_connection_type: marketability?.contractType?.toLowerCase() || '',
        ...availability && {
            available_technologies: getAvailableProductTypes(availability),
        },
        ...marketability?.selfInstallationUnavailableReason && {
            self_installation_unavailable_reason: marketability.selfInstallationUnavailableReason,
        },
    };

    return fastestTariff ? {
        ...payload,
        available_bandwidth: `${fastestTariff.download} mbit`,
        recommended_products: [fastestTariff.id],
        selected_product_available: isSelectedAvailable(state),
    } : {
        ...payload,
        recommended_products: ['gigacube'],
        selected_product_available: false,
    };
};

/**
 * Returns the tracking type for error type
 */
export const getErrorType = (type: ErrorType) => {
    switch (type) {
        case ErrorType.AVC_ERROR:
        case ErrorType.CMS_CONFIG:
        case ErrorType.JARVIS:
        case ErrorType.ZP_API:
            return TRACKING_ERROR_TYPE_TECHNICAL;
        case ErrorType.LM_VALIDATION_ERROR:
        case ErrorType.NOT_FOUND_ADDRESS:
        case ErrorType.NOT_UNIQUE_ADDRESS:
            return TRACKING_ERROR_TYPE_VALIDATION;
    }
};
